import * as React from "react";
import { cn } from "@/lib/utils";
import { motion } from 'framer-motion';
import { Building2, Database } from "lucide-react";

const Table = React.forwardRef(({ className, ...props }, ref) => (
    <div className="relative w-full overflow-auto">
        <table
            ref={ref}
            className={cn("w-full caption-bottom text-sm", className)}
            {...props} />
    </div>
))
Table.displayName = "Table"

const TableHeader = React.forwardRef(({ className, ...props }, ref) => (
    <thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props} />
))
TableHeader.displayName = "TableHeader"

const TableBody = React.forwardRef(({ className, ...props }, ref) => (
    <tbody
        ref={ref}
        className={cn("[&_tr:last-child]:border-0", className)}
        {...props} />
))
TableBody.displayName = "TableBody"

const TableFooter = React.forwardRef(({ className, ...props }, ref) => (
    <tfoot
        ref={ref}
        className={cn("border-t bg-muted/50 font-medium [&>tr]:last:border-b-0", className)}
        {...props} />
))
TableFooter.displayName = "TableFooter"

const TableRow = React.forwardRef(({ className, ...props }, ref) => (
    <tr
        ref={ref}
        className={cn(
            "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
            className
        )}
        {...props} />
))
TableRow.displayName = "TableRow"

const TableHead = React.forwardRef(({ className, ...props }, ref) => (
    <th
        ref={ref}
        className={cn(
            "h-10 px-2 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
            className
        )}
        {...props} />
))
TableHead.displayName = "TableHead"

const TableCell = React.forwardRef(({ className, ...props }, ref) => (
    <td
        ref={ref}
        className={cn(
            "p-2 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
            className
        )}
        {...props} />
))
TableCell.displayName = "TableCell"

const TableCaption = React.forwardRef(({ className, ...props }, ref) => (
    <caption
        ref={ref}
        className={cn("mt-4 text-sm text-muted-foreground", className)}
        {...props} />
))
TableCaption.displayName = "TableCaption"


const DataTable = ({ data,
    columns,
    htrClass,
    trClass,
    thClass,
    tdClass,
    ExpandedRow,
    loading,
    emptyText = "No record found",
    isAplyingFilter = false,
    resetFilters = () => { },
    Icon = Database,
    subText = "Try adjusting your search or filter criteria",
    boxClass = "",
    onScroll = () => { }
}) => (
    <>
        <div className={"overflow-auto overflow-x-auto max-w-full " + boxClass} onScroll={onScroll} style={((data?.length === 0) || loading) ? { overflow: "hidden" } : null}>
            <table className="w-full">
                <thead>
                    <tr className={htrClass || "border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50"}>
                        {columns.map((col) => (
                            <th key={col.key} className={col.thClass || thClass || "px-3 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider  whitespace-nowrap"}>
                                {typeof col?.label === "function" ? col?.label() : col?.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {data.map((row, index) => (
                        <>
                            <motion.tr
                                key={row.id}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.2, delay: index * 0.03 }}
                                className={trClass || "hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"}
                            >
                                {columns.map((col) => (
                                    <td key={col.key} className={col.tdClass || tdClass || "px-3 py-3"}>
                                        {col?.render ? col?.render(row, index) : row?.[col?.key]}
                                    </td>
                                ))}
                            </motion.tr>
                            {ExpandedRow ? ExpandedRow?.(row, index) : null}
                        </>
                    ))}
                </tbody>
            </table>
            {(data || [])?.length === 0 && !loading && (
                <div className="p-12 text-center">
                    <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Icon className="w-8 h-8 text-slate-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                        {emptyText}
                    </h3>
                    {isAplyingFilter &&
                        <>
                            <p className="text-slate-500 dark:text-slate-400 mb-6">
                                {subText || ""}
                            </p>
                            <button
                                onClick={() => {
                                    resetFilters()
                                }}
                                className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-medium transition-colors"
                            >
                                Clear Filters
                            </button>
                        </>
                    }
                </div>
            )}
            {(data || [])?.length === 0 && loading && (
                <div className="p-12 text-center">

                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                        Loading...
                    </h3>

                </div>
            )}

        </div>

    </>
);

export {
    Table,
    TableHeader,
    TableBody,
    TableFooter,
    TableHead,
    TableRow,
    TableCell,
    TableCaption,
    DataTable
}
